"use client";

import { useSearchParams } from "next/navigation";
import { Suspense } from "react";

import { ResearcherSignIn } from "@/components/auth/researcher-sign-in";

import type { BookingAttribution } from "./booking-lead";
import { ResearcherBooking } from "./researcher-booking";
import {
  type JoinableOrganizationSummary,
  ResearcherJoinRequest,
} from "./researcher-join-request";

interface ResearcherLoginFormProps {
  signedInAs?: string;
  verifiedEmail?: string;
  attribution?: BookingAttribution;
  joinableOrganization?: JoinableOrganizationSummary;
}

function ResearcherLoginContent({
  signedInAs,
  verifiedEmail,
  attribution,
  joinableOrganization,
}: ResearcherLoginFormProps) {
  const searchParams = useSearchParams();
  const returnUrl = searchParams?.get("returnUrl") ?? undefined;
  const ref = searchParams?.get("ref") ?? undefined;
  const signIn = searchParams?.get("signin") === "1";
  const bookingDetails = searchParams?.get("booking") ?? undefined;

  // Three ways to reach the form instead of the calendar. `signin=1` is the
  // "already have an account" link. A returnUrl means someone is mid-flow (the
  // welcome step, MCP consent) and needs to authenticate, not book. A `ref`
  // token means a partner already referred them, so they keep the direct path
  // their referrer's commission depends on.
  //
  // None of them applies to someone already signed in: the page only renders
  // this for a session with no organization, and offering that person a
  // sign-in form is the one thing they do not need.
  if (!signedInAs && (signIn || returnUrl || ref)) {
    return (
      <ResearcherSignIn
        returnUrl={returnUrl}
        referralToken={ref}
        initialEmail={searchParams?.get("email") ?? undefined}
      />
    );
  }
  // Their company already has an organization here, so the request to join it
  // replaces the booking form outright: a call would only end in a second
  // tenant for the same company. Only set for a signed-in, verified business
  // email that has not been denied, so the sign-in branch above never meets it.
  if (signedInAs && joinableOrganization) {
    return (
      <ResearcherJoinRequest
        organization={joinableOrganization}
        signedInAs={signedInAs}
      />
    );
  }

  return (
    <ResearcherBooking
      key={bookingDetails}
      bookingDetails={bookingDetails}
      signInHref="/researchers/login?signin=1"
      signedInAs={signedInAs}
      verifiedEmail={verifiedEmail}
      verificationFailed={Boolean(searchParams?.get("error"))}
      attribution={attribution}
    />
  );
}

export function ResearcherLoginForm(props: ResearcherLoginFormProps) {
  return (
    <Suspense>
      <ResearcherLoginContent {...props} />
    </Suspense>
  );
}
